import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
import { useAuth } from '../../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../../utils/config';
import { AdminUser } from '../../../../types/admin-v2';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import './RFQCommunicationDashboard.scss';

interface RFQCommunicationDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

type ConversationDirection = 'sent' | 'received';

interface SupplierSummary {
  supplierId: string;
  supplierReadableId: string;
  name: string;
  primaryEmail: string;
  region: string;
  totalMessages: number;
  lastMessageAt?: string | null;
  lastMessageDirection?: ConversationDirection | null;
  lastMessageSubject?: string | null;
  lastMessagePreview?: string | null;
  approvalStatus?: string;
}

interface ConversationMessage {
  id: string;
  direction: ConversationDirection;
  subject: string;
  preview: string;
  body: string;
  bodyFull?: string | null;
  sentAt?: string | null;
  senderEmail: string;
  toRecipients: string[];
  ccRecipients: string[];
  attachments: Array<Record<string, unknown>>;
  replyToEmailId?: string | null;
  messageId?: string | null;
  agentProcessed?: boolean;
  agentUpdates?: Array<{
    action: 'created' | 'updated';
    product_name: string;
    product_id: string;
    fields?: string[];
    supplier_product_id?: string;
  }>;
}

interface MailboxInfo {
  id: string | null;
  threadId: string | null;
  isDone: boolean;
  doneAt: string | null;
  lastActivity: string | null;
}

interface CommunicationProduct {
  requestProductId: string | null;
  supplierProductId: string | null;
  productId: string | null;
  lineNumber: number | string;
  productName: string;
  price: number | string | null;
  currency: string;
  leadTime: string;
  paymentTerms: string;
  deliveryCost: string;
  updatedByAgent?: boolean;
  createdByAgent?: boolean;
}

const DEFAULT_SUBJECT = 'RFQ Communication';

const CURRENCY_OPTIONS = ['TRY', 'USD', 'EUR', 'GBP'];

const getRegionDefaultCurrency = (region?: string | null): string => {
  if (!region) return '';
  const map: Record<string, string> = {
    tr: 'TRY',
    en: 'USD',
    de: 'EUR'
  };
  return map[region.toLowerCase()] || '';
};

const AGENT_OPTIONS = [
  {
    value: 'price_request',
    label: 'Price Request',
    description: 'Request a full quotation including pricing and delivery details.'
  },
  {
    value: 'reminder',
    label: 'Reminder',
    description: 'Send a polite reminder when a supplier has not responded yet.'
  },
  {
    value: 'inquiry',
    label: 'Inquiry',
    description: 'Ask follow-up questions or clarify previous correspondence.'
  }
] as const;

const formatText = (value?: string) => {
  if (!value) return '—';
  return value;
};

const formatNumber = (value: number | string | null | undefined, currency?: string) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  const numValue = Number(value);
  const formatted = numValue.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const truncateText = (value: string, maxLength = 180) => {
  if (!value) {
    return '';
  }
  return value.length > maxLength ? `${value.slice(0, maxLength).trim()}…` : value;
};

const ensureReplyPrefix = (value: string): string => {
  if (!value) {
    return `Re: ${DEFAULT_SUBJECT}`;
  }
  return /^re:/i.test(value) ? value : `Re: ${value}`;
};

const normalizeEmail = (value: string) => value.trim();

// Helper to strip day(s) suffix from values for editing
const stripDaySuffix = (value: string): string => {
  if (!value) return '';
  return value.replace(/\s*day\(s\)\s*/gi, '').trim();
};

const RFQCommunicationDashboard: React.FC<RFQCommunicationDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const location = useLocation();
  const { requestId: paramRequestId, supplierId: supplierIdParam } = useParams<{ requestId: string; supplierId?: string }>();

  // Extract requestId from URL if not in params (support both /dashboard/rfqs/open/:id and /dashboard/user/rfq/:id)
  const requestId = useMemo(() => {
    if (paramRequestId) return paramRequestId;
    const pathParts = location.pathname.split('/');
    const openIndex = pathParts.indexOf('open');
    const rfqIndex = pathParts.indexOf('rfq'); // For /dashboard/user/rfq/ pattern
    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      return pathParts[openIndex + 1];
    } else if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      return pathParts[rfqIndex + 1];
    }
    return null;
  }, [paramRequestId, location.pathname]);

  // Navigation actions
  const navInfo = useMemo(() => getRFQNavigationInfo(currentRoute), [currentRoute]);
  const navigationActions = useMemo(() => {
    const hasPreviousPage = navInfo?.previousPage;
    const hasNextPage = navInfo?.nextPage;

    if (!hasPreviousPage && !hasNextPage) {
      return undefined;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasPreviousPage && navInfo?.previousPage && (
            <button
              onClick={() => {
                if (navInfo && navInfo.previousPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage));
                }
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              ← Back to {getRFQPageName(navInfo.previousPage)}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasNextPage && navInfo?.nextPage && (
            <button
              onClick={() => {
                if (navInfo.nextPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage));
                }
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Next to {navInfo.nextPage ? getRFQPageName(navInfo.nextPage) : ''} →
            </button>
          )}
        </div>
      </div>
    );
  }, [navInfo, onNavigate]);

  // Determine base route from initial pathname (set once on mount, doesn't change with navigation) 
  const [baseRoute] = useState(() => {
    const isUserRoute = location.pathname.startsWith('/dashboard/user/rfq/');
    const route = isUserRoute ? '/dashboard/user/rfq' : '/dashboard/rfqs/open';
    console.log('🔧 RFQCommunicationDashboard: Setting baseRoute on mount:', {
      pathname: location.pathname,
      isUserRoute,
      baseRoute: route
    });
    return route;
  });
  const isBasicPlanRoute = baseRoute === '/dashboard/user/rfq';

  const [supplierSummaries, setSupplierSummaries] = useState<SupplierSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [activeSupplierId, setActiveSupplierId] = useState<string | null>(null);
  const latestSupplierIdRef = useRef<string | null>(null);

  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [availableRecipients, setAvailableRecipients] = useState<string[]>([]);

  const [composerTo, setComposerTo] = useState<string>('');
  const [composerSubject, setComposerSubject] = useState<string>(DEFAULT_SUBJECT);
  const [composerBody, setComposerBody] = useState<string>('');
  const [composerCcList, setComposerCcList] = useState<string[]>([]);
  const [ccInputValue, setCcInputValue] = useState<string>('');
  const [replyToEmailId, setReplyToEmailId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccessMessage, setSendSuccessMessage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // AI Reply states
  const [isGeneratingAI, setIsGeneratingAI] = useState<boolean>(false);
  const [mailboxInfo, setMailboxInfo] = useState<MailboxInfo | null>(null);
  const [toggleDoneLoading, setToggleDoneLoading] = useState<boolean>(false);
  const [conversationBanner, setConversationBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedAgentOption, setSelectedAgentOption] = useState<string>('price_request');

  // Drafts state (mapped by productId)
  const [drafts, setDrafts] = useState<Record<string, any>>({});
  const [draftsLoading, setDraftsLoading] = useState<boolean>(false);

  const [products, setProducts] = useState<CommunicationProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState<boolean>(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productDraft, setProductDraft] = useState({
    price: '',
    currency: '',
    leadTime: '',
    paymentTerms: '',
    deliveryCost: ''
  });
  const [isSavingProduct, setIsSavingProduct] = useState<boolean>(false);
  const [productSaveError, setProductSaveError] = useState<string | null>(null);
  const [productSaveSuccess, setProductSaveSuccess] = useState<string | null>(null);

  const agentLabelMap = useMemo(
    () =>
      AGENT_OPTIONS.reduce((acc, option) => {
        acc[option.value] = option.label;
        return acc;
      }, {} as Record<string, string>),
    []
  );

  const selectedAgent = useMemo(
    () => AGENT_OPTIONS.find((option) => option.value === selectedAgentOption) || AGENT_OPTIONS[0],
    [selectedAgentOption]
  );

  useEffect(() => {
    if (!sendSuccessMessage) {
      return;
    }
    const timeout = setTimeout(() => setSendSuccessMessage(null), 3500);
    return () => clearTimeout(timeout);
  }, [sendSuccessMessage]);

  const activeSupplier = useMemo(
    () => supplierSummaries.find((supplier) => supplier.supplierId === activeSupplierId) || null,
    [supplierSummaries, activeSupplierId]
  );

  const isConversationDone = mailboxInfo?.isDone ?? false;

  useEffect(() => {
    if (!conversationBanner) {
      return;
    }
    const timeout = setTimeout(() => setConversationBanner(null), 3500);
    return () => clearTimeout(timeout);
  }, [conversationBanner]);

  useEffect(() => {
    if (!productSaveSuccess) {
      return;
    }
    const timeout = setTimeout(() => setProductSaveSuccess(null), 3000);
    return () => clearTimeout(timeout);
  }, [productSaveSuccess]);

  const resetComposer = useCallback(() => {
    setComposerTo('');
    setComposerSubject(DEFAULT_SUBJECT);
    setComposerBody('');
    setComposerCcList([]);
    setCcInputValue('');
    setReplyToEmailId(null);
    setSendError(null);
    setSendSuccessMessage(null);
    setSelectedFiles([]);
  }, []);

  const applySupplierContextReset = useCallback(() => {
    resetComposer();
    setMailboxInfo(null);
    setConversationBanner(null);
    setToggleDoneLoading(false);
    setEditingProductId(null);
    setProductDraft({
      price: '',
      currency: '',
      leadTime: '',
      paymentTerms: '',
      deliveryCost: ''
    });
    setProductSaveError(null);
    setProductSaveSuccess(null);
    setIsSavingProduct(false);
  }, [resetComposer]);

  const fetchSupplierSummary = useCallback(async (isBackgroundRefresh = false) => {
    if (!requestId || !token) {
      return;
    }

    if (!isBackgroundRefresh) {
      setSummaryLoading(true);
    }
    setSummaryError(null);

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_SUMMARY(requestId), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load communication summary');
      }

      const summaries: SupplierSummary[] = (result.data || []).map((item: any) => ({
        supplierId: item.supplier_id,
        supplierReadableId: item.supplier_readable_id,
        name: item.name,
        primaryEmail: item.primary_email,
        region: item.region,
        totalMessages: item.total_messages || 0,
        lastMessageAt: item.last_message_at,
        lastMessageDirection: item.last_message_direction,
        lastMessageSubject: item.last_message_subject,
        lastMessagePreview: item.last_message_preview
      }));

      setSupplierSummaries(summaries);

      if (summaries.length === 0) {
        setActiveSupplierId(null);
        applySupplierContextReset();
      }

    } catch (error: any) {
      console.error('Error loading communication summary:', error);
      setSummaryError(error.message || 'Failed to load communication summary');
      setSupplierSummaries([]);
      setActiveSupplierId(null);
      applySupplierContextReset();
    } finally {
      if (!isBackgroundRefresh) {
        setSummaryLoading(false);
      }
    }
  }, [requestId, token, applySupplierContextReset]);

  const fetchConversation = useCallback(async (supplierId: string, preserveScroll = false) => {
    if (!requestId || !token) {
      return;
    }

    // Preserve scroll position during auto-refresh
    let savedScrollTop = 0;
    let savedScrollHeight = 0;
    const timelineElement = document.querySelector('.rfq-communication__timeline');

    if (preserveScroll && timelineElement) {
      savedScrollTop = timelineElement.scrollTop;
      savedScrollHeight = timelineElement.scrollHeight;
    }

    // Only show loading on initial fetch, not during auto-refresh
    if (!preserveScroll) {
      setMessagesLoading(true);
      setMessagesError(null);
    }

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, supplierId), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load communication messages');
      }

      const messages: ConversationMessage[] = (result.data?.messages || []).map((item: any) => ({
        id: item.id,
        direction: item.direction,
        subject: item.subject,
        preview: item.preview || '',
        body: item.body || '',
        bodyFull: item.body_full,
        sentAt: item.sent_at,
        senderEmail: item.sender_email || '',
        toRecipients: item.to_recipients || [],
        ccRecipients: item.cc_recipients || [],
        attachments: item.attachments || [],
        replyToEmailId: item.reply_to_email_id,
        messageId: item.message_id
      }));

      console.log(`📧 Frontend: Received ${messages.length} messages from backend`);
      messages.forEach((msg, idx) => {
        console.log(`  ${idx + 1}. ${msg.direction} - ${msg.subject} - body length: ${msg.body?.length || 0}`);
      });

      setConversationMessages(messages);
      const mailboxData = result.data?.mailbox || {};
      setMailboxInfo({
        id: mailboxData.id || null,
        threadId: mailboxData.thread_id || mailboxData.threadId || null,
        isDone: Boolean(mailboxData.is_done ?? mailboxData.isDone ?? false),
        doneAt: mailboxData.done_at || mailboxData.doneAt || null,
        lastActivity: mailboxData.last_activity || mailboxData.lastActivity || null
      });

      // Store available recipients for dropdown
      const recipients = result.data?.available_recipients || [];
      setAvailableRecipients(recipients);

      // Only update composer state on initial fetch, not during auto-refresh
      if (!preserveScroll) {
        const latest = messages.length > 0 ? messages[messages.length - 1] : null;
        const suggestedSubject = latest?.subject
          ? ensureReplyPrefix(latest.subject)
          : ensureReplyPrefix(DEFAULT_SUBJECT);

        if (!replyToEmailId) {
          setComposerSubject(suggestedSubject);
        }
      }

      // Restore scroll position after DOM updates
      if (preserveScroll && timelineElement) {
        requestAnimationFrame(() => {
          const newScrollHeight = timelineElement.scrollHeight;
          const scrollDiff = newScrollHeight - savedScrollHeight;

          // If new messages were added, adjust scroll to keep current position
          if (scrollDiff > 0) {
            timelineElement.scrollTop = savedScrollTop + scrollDiff;
          } else {
            timelineElement.scrollTop = savedScrollTop;
          }
        });
      }

    } catch (error: any) {
      console.error('Error loading conversation messages:', error);
      setMessagesError(error.message || 'Failed to load conversation');
      setConversationMessages([]);
      setMailboxInfo(null);
    } finally {
      // Only clear loading state on initial fetch
      if (!preserveScroll) {
        setMessagesLoading(false);
      }
    }
  }, [requestId, token, replyToEmailId]);

  const fetchSupplierProducts = useCallback(async (supplierId: string) => {
    if (!requestId || !token || !supplierId) {
      setProducts([]);
      setProductsError(null);
      setProductsLoading(false);
      return;
    }

    setProductsLoading(true);
    setProductsError(null);

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_SUPPLIER_PRODUCTS(requestId, supplierId), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load supplier products');
      }

      const rows: CommunicationProduct[] = (result.data || []).map((item: any) => {
        let priceValue: number | string | null = null;
        if (item.price !== undefined && item.price !== null && item.price !== '') {
          if (typeof item.price === 'number') {
            priceValue = item.price;
          } else {
            const parsed = Number(item.price);
            priceValue = Number.isNaN(parsed) ? String(item.price) : parsed;
          }
        }

        return {
          requestProductId: item.request_product_id || null,
          supplierProductId: item.supplier_product_id || null,
          productId: item.product_id || null,
          lineNumber: item.line_number ?? '-',
          productName: item.product_name || '—',
          price: priceValue,
          currency: item.currency || '',
          leadTime: item.lead_time || '',
          paymentTerms: item.payment_terms || '',
          deliveryCost: item.delivery_cost || ''
        };
      });

      // Ensure latest supplier selection still matches response
      if (latestSupplierIdRef.current !== supplierId) {
        return;
      }

      setProducts(rows);
    } catch (error: any) {
      console.error('Error loading supplier products:', error);
      if (activeSupplierId === supplierId) {
        setProductsError(error.message || 'Failed to load supplier products');
        setProducts([]);
      }
    } finally {
      if (latestSupplierIdRef.current === supplierId) {
        setProductsLoading(false);
      }
    }
  }, [requestId, token]);

  useEffect(() => {
    fetchSupplierSummary();
  }, [fetchSupplierSummary]);

  useEffect(() => {
    latestSupplierIdRef.current = activeSupplierId;
  }, [activeSupplierId]);

  useEffect(() => {
    if (!activeSupplierId) {
      latestSupplierIdRef.current = null;
      setProducts([]);
      setProductsError(null);
      setProductsLoading(false);
      return;
    }

    latestSupplierIdRef.current = activeSupplierId;
    fetchSupplierProducts(activeSupplierId);
  }, [activeSupplierId, fetchSupplierProducts]);

  useEffect(() => {
    if (!requestId || supplierSummaries.length === 0) {
      return;
    }

    const normalizedParam = supplierIdParam ?? null;
    const isValidSupplierParam = normalizedParam && supplierSummaries.some((s) => s.supplierId === normalizedParam);

    // If URL has valid supplierId, just set it (don't update URL)
    if (isValidSupplierParam) {
      if (activeSupplierId !== normalizedParam) {
        setActiveSupplierId(normalizedParam);
        applySupplierContextReset();
      }
      return; // Don't modify URL if it already has valid supplier ID
    }

    // URL doesn't have supplier ID - need to add it
    // But ONLY update URL if current route matches our expected base route pattern
    // This prevents changing from user route to admin route or vice versa
    const isCurrentRouteMatchingBase =
      currentRoute.startsWith(baseRoute) ||
      currentRoute.includes('/communication');

    if (!isCurrentRouteMatchingBase) {
      // Current URL doesn't match our base route, don't modify it
      return;
    }

    // Select fallback supplier
    const fallbackSupplierId =
      activeSupplierId && supplierSummaries.some((s) => s.supplierId === activeSupplierId)
        ? activeSupplierId
        : supplierSummaries[0].supplierId;

    // Set the fallback supplier
    if (activeSupplierId !== fallbackSupplierId) {
      setActiveSupplierId(fallbackSupplierId);
      applySupplierContextReset();
    }

    // Update URL to add supplier ID
    const targetRoute = `${baseRoute}/${requestId}/communication/${fallbackSupplierId}`;
    if (currentRoute !== targetRoute) {
      console.log('🔧 RFQCommunicationDashboard: Navigating to add supplier ID:', {
        from: currentRoute,
        to: targetRoute,
        baseRoute,
        fallbackSupplierId
      });
      onNavigate(targetRoute);
    }
  }, [
    supplierSummaries,
    supplierIdParam,
    activeSupplierId,
    requestId,
    onNavigate,
    currentRoute,
    applySupplierContextReset,
    baseRoute
  ]);

  useEffect(() => {
    if (!activeSupplierId) {
      setConversationMessages([]);
      return;
    }
    fetchConversation(activeSupplierId);
  }, [activeSupplierId, fetchConversation]);

  useEffect(() => {
    if (!activeSupplierId) {
      return;
    }

    const interval = setInterval(() => {
      // Preserve scroll position during auto-refresh
      fetchConversation(activeSupplierId, true);
      fetchSupplierSummary(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [activeSupplierId, fetchConversation, fetchSupplierSummary]);

  const handleSupplierSelect = (supplierId: string) => {
    if (activeSupplierId !== supplierId) {
      setActiveSupplierId(supplierId);
      applySupplierContextReset();
    }
    if (requestId) {
      const targetRoute = `${baseRoute}/${requestId}/communication/${supplierId}`;
      if (currentRoute !== targetRoute) {
        onNavigate(targetRoute);
      }
    }
  };

  const handleRemoveCc = (email: string) => {
    setComposerCcList((prev) => prev.filter((item) => item !== email));
  };

  const getProductRowKey = (product: CommunicationProduct) =>
    product.requestProductId || product.productId || `row-${product.lineNumber}`;

  const handleStartProductEdit = (product: CommunicationProduct) => {
    const rowKey = getProductRowKey(product);
    setEditingProductId(rowKey);
    const defaultCurrency = getRegionDefaultCurrency(activeSupplier?.region);

    // KEY CHANGE: Lookup draft by requestProductId (stable RFQ line item)
    const draft = product.requestProductId ? drafts[product.requestProductId]?.draft_data : null;

    // Strip day(s) suffix from values so user only sees/edits numbers
    setProductDraft({
      price: draft?.price !== undefined ? String(draft.price) : (product.price ? String(product.price) : ''),
      currency: (draft?.currency || product.currency || defaultCurrency || '').toUpperCase(),
      leadTime: stripDaySuffix(draft?.leadTime || product.leadTime || ''),
      paymentTerms: stripDaySuffix(draft?.paymentTerms || product.paymentTerms || ''),
      deliveryCost: stripDaySuffix(draft?.deliveryCost || product.deliveryCost || '')
    });
    setProductSaveError(null);
    setProductSaveSuccess(null);
  };

  const handleCancelProductEdit = () => {
    setEditingProductId(null);
    setProductDraft({
      price: '',
      currency: '',
      leadTime: '',
      paymentTerms: '',
      deliveryCost: ''
    });
    setProductSaveError(null);
    setProductSaveSuccess(null);
    setIsSavingProduct(false);
  };

  const handleProductDraftChange = (field: 'price' | 'currency' | 'leadTime' | 'paymentTerms' | 'deliveryCost', value: string) => {
    const nextValue = field === 'currency' ? value.toUpperCase() : value;
    setProductDraft((prev) => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const handleSaveProduct = async () => {
    if (!editingProductId || !activeSupplierId) return;

    // Find the product we are editing
    const productIndex = products.findIndex(p => getProductRowKey(p) === editingProductId);
    if (productIndex === -1) return;

    const product = products[productIndex];
    let targetId = product.supplierProductId;

    setIsSavingProduct(true);
    setProductSaveError(null);
    setProductSaveSuccess(null);

    try {
      // If no supplier product ID exists, create the product first
      if (!targetId) {
        // Create new supplier product
        const createResponse = await fetch(API_ENDPOINTS.SUPPLIER_PRODUCTS(activeSupplierId), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_name: product.productName,
            product_category: 'General', // Default category
            request_product_id: product.requestProductId, // Link to request product if possible, backend ignored if not in schema but good for intent
            region: activeSupplier?.region || 'tr' // Make sure to pass the region as user requested
          })
        });

        const createResult = await createResponse.json();
        if (!createResponse.ok || !createResult.success) {
          throw new Error(createResult.error || 'Failed to create new supplier product');
        }

        targetId = createResult.supplier_product_id;

        // Update local state with the new ID so future actions use it
        // We do this immutably
        const updatedProducts = [...products];
        updatedProducts[productIndex] = {
          ...product,
          supplierProductId: targetId
        };
        setProducts(updatedProducts);
      }

      if (!targetId) {
        throw new Error("Failed to obtain a valid product ID.");
      }

      // KEY CHANGE: Use request_product_id as primary key for drafts
      const payload = {
        request_id: requestId,
        supplier_id: activeSupplierId,
        request_product_id: product.requestProductId,  // Primary key for draft
        supplier_product_id: targetId,                 // Reference to supplier_product
        draft_data: {
          price: productDraft.price ? Number(productDraft.price) : null,
          currency: productDraft.currency,
          // Save raw values without suffix - display logic will add suffix
          leadTime: productDraft.leadTime.trim(),
          paymentTerms: productDraft.paymentTerms.trim(),
          deliveryCost: productDraft.deliveryCost.trim()
        }
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/supplier-products/draft`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save product draft');
      }

      setProductSaveSuccess('Draft saved locally.');
      // Update drafts state keyed by requestProductId
      if (product.requestProductId) {
        setDrafts(prev => ({
          ...prev,
          [product.requestProductId!]: {
            draft_data: payload.draft_data,
            supplier_product_id: targetId
          }
        }));
      }

      setTimeout(() => {
        setEditingProductId(null);
        setProductSaveSuccess(null);
      }, 1000);

    } catch (error: any) {
      console.error('Error saving supplier product draft:', error);
      setProductSaveError(error.message || 'Failed to save product draft');
      setProductSaveSuccess(null);
    } finally {
      setIsSavingProduct(false);
    }
  };

  const addCcAddress = useCallback((email: string) => {
    const normalized = normalizeEmail(email);
    if (!normalized) {
      return;
    }
    setComposerCcList((prev) => {
      if (prev.includes(normalized)) {
        return prev;
      }
      return [...prev, normalized];
    });
  }, []);

  const handleCcInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      if (ccInputValue.trim()) {
        addCcAddress(ccInputValue.replace(/[;,]+$/, ''));
        setCcInputValue('');
      }
    } else if (event.key === 'Backspace' && ccInputValue === '' && composerCcList.length > 0) {
      setComposerCcList((prev) => prev.slice(0, prev.length - 1));
    }
  };

  const handleCcInputBlur = () => {
    // When user leaves the input field, add any remaining email if valid
    if (ccInputValue.trim()) {
      addCcAddress(ccInputValue.trim());
      setCcInputValue('');
    }
  };

  const handleAttachmentClick = () => {
    if (isConversationDone) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isConversationDone) {
      return;
    }
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      // Reset input so same file can be selected again
      event.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const generateAIReply = useCallback(
    async (agentType?: string) => {
      if (!requestId || !activeSupplierId || !token) {
        return;
      }

      if (isGeneratingAI) {
        return;
      }

      if (isConversationDone) {
        setSendError('Conversation is finished. Reopen it to generate a new reply.');
        return;
      }

      try {
        setIsGeneratingAI(true);
        setSendError(null);
        setSendSuccessMessage(null);

        const payload = agentType ? { agent_type: agentType } : {};
        const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_AI_REPLY(requestId, activeSupplierId), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
          const errorMessage = result.error || 'Failed to generate AI reply';
          throw new Error(errorMessage);
        }

        const responseData = result.data || {};
        const serverAgentTypeRaw = typeof responseData.agent_type === 'string' ? responseData.agent_type.toLowerCase() : undefined;
        const resolvedAgentType =
          (serverAgentTypeRaw && agentLabelMap[serverAgentTypeRaw] ? serverAgentTypeRaw : undefined) ||
          (agentType && agentLabelMap[agentType] ? agentType : 'price_request');
        const agentLabel = agentLabelMap[resolvedAgentType] || agentLabelMap['price_request'];
        const manualOverride = Boolean(responseData.manual_override);
        const reasoningText = truncateText(responseData.reasoning || '');
        const originLabel = manualOverride ? 'manual override' : 'auto routing';

        let successMessage = `AI email content generated (${agentLabel}, ${originLabel}).`;
        if (reasoningText) {
          successMessage += ` Reason: ${reasoningText}`;
        }

        setComposerBody(responseData.body_html || responseData.body || '');
        setSendSuccessMessage(successMessage);

        if (!agentType && agentLabelMap[resolvedAgentType]) {
          setSelectedAgentOption(resolvedAgentType);
        }
      } catch (error: any) {
        console.error('Error generating AI reply:', error);
        setSendError(error.message || 'Failed to generate AI reply');
      } finally {
        setIsGeneratingAI(false);
      }
    },
    [
      requestId,
      activeSupplierId,
      token,
      isGeneratingAI,
      isConversationDone,
      agentLabelMap
    ]
  );

  const handleReplyWithAI = useCallback(() => {
    void generateAIReply();
  }, [generateAIReply]);

  const handleReplyWithAgent = useCallback(() => {
    void generateAIReply(selectedAgentOption);
  }, [generateAIReply, selectedAgentOption]);

  const handleComposerSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!requestId || !activeSupplierId || !token) {
      return;
    }

    if (isConversationDone) {
      setSendError('Conversation is finished. Reopen it to send a new message.');
      return;
    }

    if (!composerTo.trim()) {
      setSendError('Recipient email is required.');
      return;
    }

    if (!composerSubject.trim() || !composerBody.trim()) {
      setSendError('Subject and message body are required.');
      return;
    }

    const latestMessage = conversationMessages.length > 0
      ? conversationMessages[conversationMessages.length - 1]
      : null;

    const effectiveReplyId = replyToEmailId || latestMessage?.id || null;

    setIsSending(true);
    setSendError(null);

    try {
      // Use FormData if files are attached, otherwise JSON
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append('to_email', composerTo.trim());
        formData.append('subject', composerSubject.trim());
        formData.append('body', composerBody.trim());
        formData.append('cc', composerCcList.join(','));
        if (effectiveReplyId) {
          formData.append('reply_to_email_id', effectiveReplyId);
        }

        // Add files
        selectedFiles.forEach((file, index) => {
          formData.append(`file${index}`, file);
        });

        const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, activeSupplierId), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
            // No Content-Type header - browser will set it with boundary for multipart/form-data
          },
          body: formData
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to send email');
        }
      } else {
        // No files - use JSON
        const payload = {
          to_email: composerTo.trim(),
          subject: composerSubject.trim(),
          body: composerBody.trim(),
          cc: composerCcList,
          reply_to_email_id: effectiveReplyId
        };

        const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, activeSupplierId), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to send email');
        }
      }

      setSendSuccessMessage('Email sent successfully.');
      resetComposer();
      await fetchConversation(activeSupplierId);
      await fetchSupplierSummary();

    } catch (error: any) {
      console.error('Error sending email:', error);
      setSendError(error.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleConversationDone = useCallback(async () => {
    if (!token || !mailboxInfo?.id || toggleDoneLoading) {
      return;
    }

    try {
      setToggleDoneLoading(true);
      const response = await fetch(API_ENDPOINTS.MAILBOX_FINISH(mailboxInfo.id), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_done: !mailboxInfo.isDone })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to update conversation status');
      }

      const updated = result.data || {};
      setMailboxInfo({
        id: updated.mailbox_id || mailboxInfo.id,
        threadId: updated.thread_id || mailboxInfo.threadId,
        isDone: Boolean(updated.is_done),
        doneAt: updated.done_at || null,
        lastActivity: updated.last_activity || mailboxInfo.lastActivity
      });

      setConversationBanner({
        type: 'success',
        message: updated.is_done ? 'Conversation marked as finished.' : 'Conversation reopened.'
      });

      fetchSupplierSummary();
    } catch (error: any) {
      console.error('Error updating conversation status:', error);
      setConversationBanner({
        type: 'error',
        message: error.message || 'Failed to update conversation status.'
      });
    } finally {
      setToggleDoneLoading(false);
    }
  }, [token, mailboxInfo, toggleDoneLoading, fetchSupplierSummary]);

  const normalizeDateValue = useCallback((value?: string | null) => {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (/[Zz]$/.test(trimmed) || /[+\-]\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      const withZ = trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`;
      return withZ.replace(/(\.\d{3})\d+Z$/, '$1Z');
    }
    return trimmed;
  }, []);

  const parseDateValue = useCallback(
    (value?: string | null): Date | null => {
      const normalized = normalizeDateValue(value);
      if (!normalized) {
        return null;
      }
      const timestamp = Date.parse(normalized);
      if (Number.isNaN(timestamp)) {
        return null;
      }
      return new Date(timestamp);
    },
    [normalizeDateValue]
  );

  const formatDateTime = useCallback(
    (value?: string | null) => {
      const parsed = parseDateValue(value);
      if (!parsed) {
        return null;
      }
      try {
        const formatter = new Intl.DateTimeFormat(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZoneName: 'short'
        });
        return formatter.format(parsed);
      } catch {
        return parsed.toLocaleString();
      }
    },
    [parseDateValue]
  );

  const doneDateFormatted = useMemo(() => {
    return formatDateTime(mailboxInfo?.doneAt);
  }, [mailboxInfo, formatDateTime]);


  const Layout = isBasicPlanRoute ? UserLayout : AdminLayout;

  return (
    <Layout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle="Communication"
      getDynamicSubItems={getDynamicSubItems}
      navigationActions={navigationActions}
    >
      <div className="rfq-communication-page-wrapper">
        <div className="rfq-communication">
          {/* Products Section - Moved here to appear before conversation */}
          <div className="rfq-communication__products-section">
            <div className="rfq-communication__products-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2>RFQ Products</h2>
                {products.length > 0 && (
                  <span>{products.length} items</span>
                )}
              </div>
            </div>

            {productSaveError && !productsLoading && (
              <div className="rfq-communication__products-state rfq-communication__products-state--error">
                {productSaveError}
              </div>
            )}
            {productSaveSuccess && !productsLoading && (
              <div className="rfq-communication__products-state rfq-communication__products-state--success">
                {productSaveSuccess}
              </div>
            )}

            {productsLoading ? (
              <div className="rfq-communication__products-state">Loading products…</div>
            ) : productsError ? (
              <div className="rfq-communication__products-state rfq-communication__products-state--error">{productsError}</div>
            ) : products.length === 0 ? (
              <div className="rfq-communication__products-state">No products found for this RFQ.</div>
            ) : (
              <div className="rfq-communication__products-table-wrapper">
                <table className="rfq-communication__products-table">
                  <thead>
                    <tr>
                      <th style={{ width: '110px' }}>Actions</th>
                      <th>Product</th>
                      <th style={{ width: '160px' }}>Price</th>
                      <th style={{ width: '160px' }}>Lead Time</th>
                      <th style={{ width: '160px' }}>Payment Terms</th>
                      <th style={{ width: '160px' }}>Delivery Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const productLabel = product.productName || '—';
                      const rowKey = getProductRowKey(product);
                      const isEditing = editingProductId === rowKey;

                      // Check for draft data
                      const draft = product.requestProductId ? drafts[product.requestProductId]?.draft_data : null;
                      const hasDraft = !!draft;

                      // Formatting helpers
                      const formatVal = (val: any) => (val !== null && val !== undefined && val !== '') ? String(val) : '—';
                      const formatPrice = (p: any, c: any) => {
                        if (p === null || p === undefined || p === '') return '—';
                        const n = Number(p);
                        const s = Number.isNaN(n) ? String(p) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return c ? `${s} ${c}` : s;
                      };

                      // Display values (DB)
                      const priceDisplay = formatPrice(product.price, product.currency);
                      const leadTimeDisplay = formatText(product.leadTime);
                      const paymentTermsDisplay = formatText(product.paymentTerms);
                      const deliveryCostDisplay = formatText(product.deliveryCost);

                      return (
                        <tr key={rowKey}>
                          <td className="rfq-communication__product-action">
                            {isEditing ? (
                              <div className="rfq-communication__product-action-group">
                                <button
                                  type="button"
                                  className="rfq-communication__product-action-btn rfq-communication__product-action-btn--primary"
                                  onClick={handleSaveProduct}
                                  disabled={isSavingProduct}
                                >
                                  {isSavingProduct ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="rfq-communication__product-action-btn"
                                  onClick={handleCancelProductEdit}
                                  disabled={isSavingProduct}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={`rfq-communication__product-action-btn ${product.supplierProductId ? 'rfq-communication__product-action-btn--edit' : 'rfq-communication__product-action-btn--add'}`}
                                onClick={() => handleStartProductEdit(product)}
                              >
                                {product.supplierProductId ? 'Edit' : '+'}
                              </button>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {product.lineNumber ? `${product.lineNumber}. ${productLabel}` : productLabel}
                              {/* AI BADGE */}
                              {(product.updatedByAgent || product.createdByAgent) && (
                                <span
                                  className="rfq-communication__agent-badge"
                                  title="This product was updated by AI Agent"
                                  style={{ fontSize: '10px', padding: '1px 4px', height: 'auto' }}
                                >
                                  AI
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Price Column */}
                          <td>
                            {isEditing ? (
                              <div className="rfq-communication__price-edit">
                                <input
                                  type="number"
                                  step="0.01"
                                  className="rfq-communication__product-input"
                                  value={productDraft.price}
                                  onChange={(event) => handleProductDraftChange('price', event.target.value)}
                                  placeholder="Price"
                                  disabled={isSavingProduct}
                                />
                                <input
                                  type="text"
                                  className="rfq-communication__product-input rfq-communication__product-input--currency"
                                  value={productDraft.currency}
                                  onChange={(event) => handleProductDraftChange('currency', event.target.value)}
                                  placeholder="Curr"
                                  disabled={isSavingProduct}
                                  maxLength={5}
                                />
                              </div>
                            ) : hasDraft ? (
                              <div className="rfq-communication__product-cell-edit">
                                <div style={{ minHeight: '22px', display: 'flex', alignItems: 'center', fontWeight: 600, color: '#2ecc71' }}>
                                  {/* DRAFT VALUE */}
                                  {formatPrice(draft.price, draft.currency)}
                                  <span style={{ fontSize: '9px', marginLeft: '4px', border: '1px solid currentColor', borderRadius: '3px', padding: '0 2px' }}>
                                    DRAFT
                                  </span>
                                </div>
                                <div className="rfq-communication__input-history-value" title="Current live value">
                                  {priceDisplay}
                                </div>
                              </div>
                            ) : (
                              // STANDARD DISPLAY
                              priceDisplay
                            )}
                          </td>
                          {/* Lead Time Column */}
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="rfq-communication__product-input"
                                value={productDraft.leadTime}
                                onChange={(event) => handleProductDraftChange('leadTime', event.target.value)}
                                placeholder="Lead time"
                                disabled={isSavingProduct}
                              />
                            ) : hasDraft ? (
                              <div className="rfq-communication__product-cell-edit">
                                <div style={{ minHeight: '22px', display: 'flex', alignItems: 'center' }}>
                                  {formatText(draft.leadTime)}
                                </div>
                                <div className="rfq-communication__input-history-value">
                                  {leadTimeDisplay}
                                </div>
                              </div>
                            ) : (
                              leadTimeDisplay
                            )}
                          </td>
                          {/* Payment Terms Column */}
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="rfq-communication__product-input"
                                value={productDraft.paymentTerms}
                                onChange={(event) => handleProductDraftChange('paymentTerms', event.target.value)}
                                placeholder="Payment terms"
                                disabled={isSavingProduct}
                              />
                            ) : hasDraft ? (
                              <div className="rfq-communication__product-cell-edit">
                                <div style={{ minHeight: '22px', display: 'flex', alignItems: 'center' }}>
                                  {formatText(draft.paymentTerms)}
                                </div>
                                <div className="rfq-communication__input-history-value">
                                  {paymentTermsDisplay}
                                </div>
                              </div>
                            ) : (
                              paymentTermsDisplay
                            )}
                          </td>
                          {/* Delivery Cost Column */}
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="rfq-communication__product-input"
                                value={productDraft.deliveryCost}
                                onChange={(event) => handleProductDraftChange('deliveryCost', event.target.value)}
                                placeholder="Delivery cost"
                                disabled={isSavingProduct}
                              />
                            ) : hasDraft ? (
                              <div className="rfq-communication__product-cell-edit">
                                <div style={{ minHeight: '22px', display: 'flex', alignItems: 'center' }}>
                                  {formatText(draft.deliveryCost)}
                                </div>
                                <div className="rfq-communication__input-history-value">
                                  {deliveryCostDisplay}
                                </div>
                              </div>
                            ) : (
                              deliveryCostDisplay
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rfq-communication__body">
            <div className="rfq-communication__left">


              <div className="rfq-communication__panel-header">
                <h2>Selected Suppliers</h2>
                <p className="rfq-communication__panel-subtitle">
                  {supplierSummaries.length} suppliers selected for this RFQ
                </p>
              </div>

              <div className="rfq-communication__supplier-list">
                {summaryLoading && (
                  <div className="rfq-communication__empty-state">Loading selected suppliers...</div>
                )}

                {summaryError && (
                  <div className="rfq-communication__error-state">{summaryError}</div>
                )}

                {!summaryLoading && !summaryError && supplierSummaries.length === 0 && (
                  <div className="rfq-communication__empty-state">
                    No suppliers selected yet. Choose suppliers from the Search Suppliers page to start communications.
                  </div>
                )}

                {!summaryLoading && !summaryError && supplierSummaries.map((supplier) => {
                  const isActive = supplier.supplierId === activeSupplierId;
                  const formattedLastActivity = supplier.lastMessageAt
                    ? formatDateTime(supplier.lastMessageAt)
                    : null;

                  return (
                    <button
                      key={supplier.supplierId}
                      className={`rfq-communication__supplier-item ${isActive ? 'is-active' : ''}`}
                      onClick={() => handleSupplierSelect(supplier.supplierId)}
                    >
                      <div className="rfq-communication__supplier-title">
                        <span className="rfq-communication__supplier-name">{supplier.name}</span>
                        <span className="rfq-communication__supplier-tag">{supplier.supplierReadableId}</span>
                      </div>
                      <div className="rfq-communication__supplier-email">{supplier.primaryEmail}</div>
                      <div className="rfq-communication__supplier-meta">
                        <span>{supplier.totalMessages} messages</span>
                        <span>
                          {formattedLastActivity
                            ? `Last activity: ${formattedLastActivity}`
                            : 'No messages yet'}
                        </span>
                      </div>
                      {supplier.lastMessageSubject && (
                        <div className="rfq-communication__supplier-preview">
                          {supplier.lastMessageSubject}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rfq-communication__right">
              {summaryLoading && !activeSupplier ? (
                <div className="rfq-communication__empty-conversation">
                  Loading suppliers...
                </div>
              ) : activeSupplier ? (
                <div className="rfq-communication__conversation">
                  <header className="rfq-communication__conversation-header">
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h2>{activeSupplier.name}</h2>
                        {activeSupplier.approvalStatus?.toLowerCase() !== 'approved' && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#fff3cd',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#856404',
                            border: '1px solid #ffeeba'
                          }}>
                            <span>⚠️ Not Approved</span>
                          </div>
                        )}
                      </div>
                      <p>
                        Primary email: {activeSupplier.primaryEmail || 'Not available'} · {conversationMessages.length} messages
                      </p>
                    </div>
                    <div className="rfq-communication__header-actions">
                      <button
                        type="button"
                        className={`rfq-communication__header-button rfq-communication__header-button--finish ${isConversationDone ? 'is-finished' : ''}`}
                        onClick={handleToggleConversationDone}
                        disabled={!mailboxInfo?.id || toggleDoneLoading}
                      >
                        {toggleDoneLoading
                          ? 'Updating...'
                          : isConversationDone
                            ? 'Reopen Conversation'
                            : 'Finish Conversation'}
                      </button>
                    </div>
                  </header>

                  {conversationBanner && (
                    <div className={`rfq-communication__banner rfq-communication__banner--${conversationBanner.type}`}>
                      {conversationBanner.message}
                    </div>
                  )}

                  {isConversationDone && (
                    <div className="rfq-communication__status-note">
                      Conversation finished{doneDateFormatted ? ` on ${doneDateFormatted}` : ''}. Reopen to continue messaging.
                    </div>
                  )}

                  <div className="rfq-communication__timeline">
                    {messagesLoading && (
                      <div className="rfq-communication__empty-timeline">Loading conversation...</div>
                    )}

                    {messagesError && (
                      <div className="rfq-communication__error-state">{messagesError}</div>
                    )}

                    {!messagesLoading && !messagesError && conversationMessages.map((message, index) => {
                      const formattedDate = message.sentAt
                        ? formatDateTime(message.sentAt) || 'Unknown time'
                        : 'Unknown time';
                      const isSent = message.direction === 'sent';

                      return (
                        <article
                          key={message.id}
                          className={`rfq-communication__message-card ${isSent ? 'is-sent' : 'is-received'}`}
                        >
                          <header className="rfq-communication__message-header">
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <h3>{message.subject || '(No subject)'}</h3>
                                {(message.agentProcessed || (message.agentUpdates && message.agentUpdates.length > 0)) && (
                                  <span
                                    className="rfq-communication__agent-badge"
                                    title="Processed by AI Agent"
                                  >
                                    🤖 AI
                                  </span>
                                )}
                              </div>
                              <p className="rfq-communication__message-meta">
                                {isSent ? 'You' : message.senderEmail || 'Supplier'} · {formattedDate}
                              </p>
                              {message.toRecipients && message.toRecipients.length > 0 && (
                                <p className="rfq-communication__message-meta rfq-communication__message-meta--recipients">
                                  <strong>To:</strong> {message.toRecipients.join(', ')}
                                </p>
                              )}
                              {message.ccRecipients && message.ccRecipients.length > 0 && (
                                <p className="rfq-communication__message-meta rfq-communication__message-meta--recipients">
                                  <strong>CC:</strong> {message.ccRecipients.join(', ')}
                                </p>
                              )}
                            </div>
                          </header>

                          <div className="rfq-communication__message-preview">
                            {(message.bodyFull || message.body || message.preview || '(No preview available)')
                              .split('\n')
                              .map((line: string, idx: number) => (
                                <div key={idx} style={{ marginBottom: line.trim() ? '4px' : '8px' }}>
                                  {line || '\u00A0'}
                                </div>
                              ))}
                          </div>

                          {message.attachments && message.attachments.length > 0 && (
                            <div className="rfq-communication__attachments">
                              {message.attachments.map((attachment: any, index: number) => {
                                const attachmentId: string = attachment.attachment_id || attachment.id || '';
                                if (!attachmentId || !requestId) return null;
                                const downloadUrl = attachment.url && attachment.url.startsWith('/api/v2/requests')
                                  ? API_ENDPOINTS.REQUEST_COMMUNICATION_ATTACHMENT(requestId, attachmentId)
                                  : `${API_ENDPOINTS.BASE_URL}/mailgun/attachments/${attachmentId}`;

                                return (
                                  <span
                                    key={`${message.id}-attachment-${index}`}
                                    className="rfq-communication__attachment-chip"
                                    onClick={() => window.open(`${downloadUrl}?token=${token}`, '_blank')}
                                    title="Click to download"
                                  >
                                    📎 {attachment.name || attachment.filename || 'Attachment'}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </article>
                      );
                    })}

                    {!messagesLoading && !messagesError && conversationMessages.length === 0 && (
                      <div className="rfq-communication__empty-timeline">
                        No messages yet. Start your first email using the composer below.
                      </div>
                    )}
                  </div>

                  <footer className="rfq-communication__composer-wrapper">
                    <form className="rfq-communication__composer" onSubmit={handleComposerSubmit}>
                      <div className="rfq-communication__composer-row">
                        <label htmlFor="rfq-composer-to">To</label>
                        <div className="rfq-communication__composer-input-group">
                          <input
                            id="rfq-composer-to"
                            type="email"
                            value={composerTo}
                            onChange={(e) => setComposerTo(e.target.value)}
                            disabled={isConversationDone}
                            placeholder="Recipient email..."
                            required
                          />
                          {availableRecipients.length > 0 && (
                            <select
                              className="rfq-communication__recipient-select"
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  setComposerTo(e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              disabled={isConversationDone}
                              title="Select from conversation recipients"
                            >
                              <option value="">Select recipient...</option>
                              {availableRecipients
                                .filter(email => email.toLowerCase() !== composerTo.toLowerCase())
                                .map((email) => (
                                  <option key={email} value={email}>
                                    {email}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                      </div>

                      <div className="rfq-communication__composer-row">
                        <label>CC</label>
                        <div className="rfq-communication__cc-container">
                          {composerCcList.map((email) => (
                            <span key={email} className="rfq-communication__cc-chip">
                              {email}
                              <button type="button" onClick={() => handleRemoveCc(email)} aria-label={`Remove ${email}`}>
                                ×
                              </button>
                            </span>
                          ))}
                          <div className="rfq-communication__cc-input-group">
                            <input
                              type="email"
                              className="rfq-communication__cc-input"
                              value={ccInputValue}
                              onChange={(event) => setCcInputValue(event.target.value)}
                              onKeyDown={handleCcInputKeyDown}
                              onBlur={handleCcInputBlur}
                              disabled={isConversationDone}
                              placeholder={composerCcList.length === 0 ? 'Add CC recipients...' : ''}
                            />
                            {availableRecipients.length > 0 && (
                              <select
                                className="rfq-communication__recipient-select"
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const email = e.target.value.trim();
                                    if (email && !composerCcList.includes(email) && email.toLowerCase() !== composerTo.toLowerCase()) {
                                      setComposerCcList([...composerCcList, email]);
                                    }
                                    e.target.value = '';
                                  }
                                }}
                                disabled={isConversationDone}
                                title="Select from conversation recipients"
                              >
                                <option value="">Add recipient...</option>
                                {availableRecipients
                                  .filter(email =>
                                    !composerCcList.includes(email) &&
                                    email.toLowerCase() !== composerTo.toLowerCase()
                                  )
                                  .map((email) => (
                                    <option key={email} value={email}>
                                      {email}
                                    </option>
                                  ))}
                              </select>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rfq-communication__composer-row">
                        <label htmlFor="rfq-composer-subject">Subject</label>
                        <input
                          id="rfq-composer-subject"
                          type="text"
                          value={composerSubject}
                          onChange={(event) => setComposerSubject(event.target.value)}
                          placeholder="Enter subject"
                        />
                      </div>

                      <div className="rfq-communication__composer-row">
                        <label htmlFor="rfq-composer-body">Message</label>
                        <textarea
                          id="rfq-composer-body"
                          value={composerBody}
                          onChange={(event) => setComposerBody(event.target.value)}
                          placeholder="Write your message..."
                          disabled={isConversationDone}
                        />
                      </div>

                      {sendError && (
                        <div className="rfq-communication__composer-feedback is-error">
                          {sendError}
                        </div>
                      )}

                      {sendSuccessMessage && (
                        <div className="rfq-communication__composer-feedback is-success">
                          {sendSuccessMessage}
                        </div>
                      )}

                      <div className="rfq-communication__composer-actions">
                        <div className="rfq-communication__attachments-area">
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                          />
                          <button
                            type="button"
                            className={`rfq-communication__attachment-button ${isConversationDone ? 'is-disabled' : ''}`}
                            onClick={handleAttachmentClick}
                            disabled={isConversationDone}
                          >
                            📎 Add attachment
                          </button>

                          {selectedFiles.length > 0 && (
                            <div className="rfq-communication__selected-files">
                              {selectedFiles.map((file, index) => (
                                <span key={index} className="rfq-communication__file-chip">
                                  {file.name}
                                  <button type="button" onClick={() => handleRemoveFile(index)} aria-label={`Remove ${file.name}`}>
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="rfq-communication__composer-buttons">
                          <div className="rfq-communication__ai-group">
                            <select
                              className="rfq-communication__ai-select"
                              value={selectedAgentOption}
                              onChange={(event) => setSelectedAgentOption(event.target.value)}
                              disabled={isGeneratingAI || isConversationDone}
                            >
                              {AGENT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="rfq-communication__ai-button rfq-communication__ai-button--manual"
                              onClick={handleReplyWithAgent}
                              disabled={isGeneratingAI || isConversationDone}
                              title={selectedAgent.description}
                            >
                              {isGeneratingAI ? 'Generating...' : 'Generate with Agent'}
                            </button>
                          </div>

                          <button type="submit" className="rfq-communication__primary-button" disabled={isSending || !composerSubject.trim() || !composerBody.trim()}>
                            {isSending ? 'Sending…' : 'Send Email'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </footer>
                </div>
              ) : (
                <div className="rfq-communication__empty-conversation">
                  Select a supplier from the left panel to view the conversation and compose a message.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RFQCommunicationDashboard;

